import differenceWith from 'lodash/differenceWith';
import {
  deserializeYesplanAPIDate,
  deserializeYesplanAPIDateAndTime,
  serializeYesplanAPIDate
} from '../libs/dateTimeUtilities';
import AbstractNotificationsCollection, {
  sortNotificationsOnSentTime
} from './YPAbstractNotificationsCollection';
import PublishOrRevokeNotification from './YPPublishOrRevokeNotification';

const isPublishOrRevokeNotification = (notification, notificationChangesProperty) =>
  notification.object &&
  notification.object.type === 'Human' &&
  notification.changes &&
  notification.changes.length > 0 &&
  notification.changes[0].property === notificationChangesProperty;

/* Process the notifications and see if a new publish date has been set.
   We only take new publish date notifications into account that were made after we authenticated:
   when a user has no <widget>publisheduntildate we do not want to set it with an old <widget>publisheduntildate.
   Return the notifications sorted on timestamp, together with the new publish date  */
export const processWidgetNotifications = (
  widgetnotificationsJSON,
  authenticationDateAndTimeObject,
  createNotificationFunction,
  notificationChangesProperty
) => {
  let newPublishedUntilDate = false;
  const widgetNotifications = widgetnotificationsJSON.flatMap((notification) => {
    if (isPublishOrRevokeNotification(notification, notificationChangesProperty)) {
      const notificationObject = new PublishOrRevokeNotification(
        notification.id,
        notification.timestamp,
        notification.isRead,
        notification.notificationMessages[0],
        notification.changes
      );
      if (notificationObject.getTimestamp().isAfter(authenticationDateAndTimeObject))
        newPublishedUntilDate = deserializeYesplanAPIDateAndTime(
          notification.changes[0].new
        );
      return notificationObject;
    }
    return createNotificationFunction(notification);
  });
  return {
    widgetNotifications,
    newPublishedUntilDate
  };
};

export default class AbstractWidgetNotificationsCollection extends AbstractNotificationsCollection {
  constructor(notifications, notificationChangesProperty, createNotificationFunction) {
    super();
    const [publishOrRevokeNotifications, widgetNotifications] =
      this.#splitNotifications(notifications);
    this.#setNewNotifications(widgetNotifications, publishOrRevokeNotifications);
    this.createNotificationFunction = createNotificationFunction;
    this.notificationChangesProperty = notificationChangesProperty;
  }

  #setNewNotifications(widgetNotifications, publishAndRevokeNotifications) {
    this.widgetNotifications = sortNotificationsOnSentTime(widgetNotifications);
    this.publishAndRevokeNotifications = sortNotificationsOnSentTime(
      publishAndRevokeNotifications
    ).reverse();
    this.widgetNotificationsGroupedByDate = this.#groupWidgetNotificationsPerDate();
    this.notifications = [...widgetNotifications, ...publishAndRevokeNotifications];
  }

  #splitNotifications(notifications) {
    return notifications.reduce(
      ([publishOrRevoke, widget], notification) =>
        notification.isPublishOrRevokeNotification()
          ? [[...publishOrRevoke, notification], widget]
          : [publishOrRevoke, [...widget, notification]],
      [[], []]
    );
  }

  #groupWidgetNotificationsPerDate() {
    const groupedByDate = {};
    this.widgetNotifications.forEach((notification) => {
      notification.getNotificationMessageDates().forEach((notificationDate) => {
        const notificationDateString = serializeYesplanAPIDate(notificationDate);
        if (groupedByDate[notificationDateString] === undefined)
          groupedByDate[notificationDateString] = [];
        const forDate = groupedByDate[notificationDateString];
        if (!forDate.includes(notification))
          groupedByDate[notificationDateString].push(notification);
      });
    });

    /* Sort the notifications on timestamp */
    Object.keys(groupedByDate).forEach((key) => {
      sortNotificationsOnSentTime(groupedByDate[key]);
    });

    /* Return an object with the sorted (date) keys and the actual object mapping
     from date (key) -> array of notifications on that date (value) */
    return groupedByDate;
  }

  getPublishAndRevokeNotifications() {
    return this.publishAndRevokeNotifications;
  }

  getWidgetNotificationsForDate(dateObject, showAll = null, maxNumber) {
    let notifications =
      this.widgetNotificationsGroupedByDate[serializeYesplanAPIDate(dateObject)];
    if (!notifications) return [];
    notifications = notifications.slice(0, maxNumber);
    if (showAll !== null)
      return showAll
        ? notifications
        : notifications.filter((notification) => !notification.getIsRead());
    return notifications;
  }

  updateNotifications(notificationsInJSON, authenticationDateAndTimeObject) {
    const { widgetNotifications, newPublishedUntilDate } = processWidgetNotifications(
      notificationsInJSON,
      authenticationDateAndTimeObject,
      this.createNotificationFunction,
      this.notificationChangesProperty
    );
    const [newPublishOrRevokeNotifications, newWidgetNotifications] =
      this.#splitNotifications(widgetNotifications);
    if (
      newPublishedUntilDate ||
      !this.equalNotifications(
        'publishAndRevokeNotifications',
        newPublishOrRevokeNotifications
      ) ||
      !this.equalNotifications('widgetNotifications', newWidgetNotifications) 
    ) {
      this.versionStamp++;
      if (
        newPublishOrRevokeNotifications.length >
          this.publishAndRevokeNotifications.length ||
        newWidgetNotifications.length > this.widgetNotifications.length 
      )
        this.versionStampQuantity++;
      if (
        newPublishOrRevokeNotifications.filter((notification) => !notification.isRead).length >
          this.publishAndRevokeNotifications.filter((notification) => !notification.isRead).length ||
        newWidgetNotifications.filter((notification) => !notification.isRead).length >
          this.widgetNotifications.filter((notification) => !notification.isRead).length
      )
        this.versionStampUnread++;
      this.deltaWidgetNotifications = differenceWith(
        newWidgetNotifications,
        this.widgetNotifications,
        (notification1, notification2) => notification1.equal(notification2)
      );
      this.#setNewNotifications(newWidgetNotifications, newPublishOrRevokeNotifications);
    }
    return newPublishedUntilDate;
  }

  hasWidgetNotificationsForDate(dateObject, maxNrToShow) {
    return this.getWidgetNotificationsForDate(dateObject, null, maxNrToShow).length !== 0;
  }

  hasUnreadNotificationsOnDate(dateObject) {
    const notificationsOnDate =
      this.widgetNotificationsGroupedByDate[serializeYesplanAPIDate(dateObject)];
    return notificationsOnDate
      ? this.widgetNotificationsGroupedByDate[serializeYesplanAPIDate(dateObject)].some(
          (notification) => !notification.getIsRead()
        )
      : false;
  }

  shouldShowWidgetChangeNotifications(includeAllNotifications) {
    return (
      this.widgetNotifications.length !== 0 &&
      (includeAllNotifications
        ? true
        : this.widgetNotifications.some((notification) => !notification.getIsRead()))
    );
  }

  shouldShowWidgetChangeNotificationsOnDate(dateObject, includeAllNotifications) {
    const notifications =
      this.widgetNotificationsGroupedByDate[serializeYesplanAPIDate(dateObject)];
    return (
      notifications &&
      notifications.length !== 0 &&
      (includeAllNotifications
        ? true
        : notifications.some((notification) => !notification.getIsRead()))
    );
  }

  shouldShowPublishAndRevokeNotifications(includeAllNotifications) {
    return this.publishAndRevokeNotifications.length !== 0;
  }

  doFunctionOnWidgetNotificationsForNonEmptyDates(fn) {
    return Object.keys(this.widgetNotificationsGroupedByDate)
      .map((dateString) => {
        const notificationsOnDate = this.widgetNotificationsGroupedByDate[dateString];
        if (notificationsOnDate.length > 0)
          return fn(deserializeYesplanAPIDate(dateString), notificationsOnDate);
        return null;
      })
      .filter((results) => results !== null);
  }
}
